import {
	createContext,
	useReducer,
	useContext,
	useEffect,
	useState,
} from 'react'
import { useAppContext } from 'contexts/AppContext'
import { useTextualContext } from 'contexts/TextualContext'

import LookupReducer from './LookupReducer'
import userService from 'services/userService'

import { useMsal } from '@azure/msal-react'
// import { useNavigate } from 'react-router'
import { EventType } from '@azure/msal-browser'
import documentService from 'services/documentService'

// Initial state
const initialValue = {
	uniqueUserId: '',
	estateId: '',
	streetAddress: '',
	loading: true,
	userLoggedIn: false,
	encodedemail: '',
	divisions: [],
	showTutorial: false,
	legalContentFlags: {
		privacyPolicy: 'no',
		termsAndCondition: 'no',
		viewTutorial: 'no',
	},
	selectedDivision: undefined,
	selectedCommunity: undefined,
	selectedLot: undefined,
	selectedUser: undefined,
	documents: [],
	preRequestBody: undefined,
	hasMultipleResponses: false,
	finalExpiryReminderEmail: '',
}

// Create context
export const LookupContext = createContext(initialValue)

// Create context to update context value
export const LookupDispatchContext = createContext(initialValue)

// Provider component
export const LookupProvider = ({ children }) => {
	const [idToken] = useState(null)

	const { showLoader, hideLoader, showAlert } = useAppContext()

	const [state, dispatch] = useReducer(LookupReducer, initialValue)
	const { instance } = useMsal()
	const { t } = useTextualContext()

	// const navigate = useNavigate()

	// Wrap object with useMemo
	// const temp = useMemo(() => ({ state, dispatch }), [state, dispatch])

	const selectEstate = async (estateId, uniqueUserId, email) => {
		try {
			const encodedEmail =
				typeof email === 'string' ? email : email[0]?.encodedEmail

			const estateDetails = await userService.lookupEstate({
				estateId,
				uniqueUserId,
				email: encodedEmail,
			})
			sessionStorage.setItem('estateDetails', JSON.stringify(estateDetails))
		} catch (error) {
			console.error('Error selecting estate:', error)
		}
	}

	useEffect(() => {
		const accounts = instance.getAllAccounts()
		if (accounts.length > 0) {
			instance.setActiveAccount(accounts[0])
		}

		let tempIdToken

		const fetchData = async () => {
			try {
				const response = await userService.userLookup({
					authCd: sessionStorage.getItem('authCode'),
					email: instance
						.getActiveAccount()
						.idTokenClaims.emails.map((email) => window.btoa(email)), // encode to base64
				})
				const encodedEmails = instance
					.getActiveAccount()
					.idTokenClaims.emails.map((email) => window.btoa(email))
				const processedData = response.map((item) => ({
					uniqueUserId: item.uniqueUserId,
					estateId: item.estateId,
					streetAddress: item.streetAddress,
					divisionMpcCd: item.divisionMpcCd,
					marketingMpcCd: item.marketingMpcCd,
					communityMpcCd: item.communityMpcCd,
					model: item.model,
					elevation: item.elevation,
					consent: item.consent,
					finalExpiryReminderEmail: item.finalExpiryReminderEmail,
				}))
				const hasMultipleResponses =
					Array.isArray(response) && response.length > 1

				if (Array.isArray(response) && response.length === 1) {
					const estateDetails = await userService.lookupEstate({
						estateId: response[0].estateId,
						uniqueUserId: response[0].uniqueUserId,
						email: encodedEmails[0],
					})
					sessionStorage.setItem('estateDetails', JSON.stringify(estateDetails))
					window.dispatchEvent(new Event('estateDetailsUpdated'))
				}

				dispatch({
					type: 'UPDATE_BASIC_DATA',
					payload: {
						users: processedData.map((item) => ({
							uniqueUserId: item.uniqueUserId,
							estateId: item.estateId,
							streetAddress: item.streetAddress,
							divisionMpcCd: item.divisionMpcCd,
							marketingMpcCd: item.marketingMpcCd,
							communityMpcCd: item.communityMpcCd,
							model: item.model,
							elevation: item.elevation,
							userLoggedIn: true,
							encodedEmail: encodedEmails[0],
							consent: item.consent,
							finalExpiryReminderEmail: item.finalExpiryReminderEmail,
						})),
						hasMultipleResponses,
					},
				})

				const estateDetails = JSON.parse(
					sessionStorage.getItem('estateDetails')
				)
				if (estateDetails) {
					const customerName = window.atob(estateDetails.customerName)
					const email = window.atob(estateDetails.email)

					const division = estateDetails.estates.divisions[0]
					const community = division.communities[0]
					const lot = community.lots[0]

					const payload = {
						division: {
							divisionMpcCd: division.divisionMpcCd,
							divisionName: division.divisionName,
						},
						community: {
							communityMpcCd: community.communityMpcCd,
							marketingMpcCd: community.marketingMpcCd,
							communityName: community.communityName,
						},
						lot: {
							lotJobID: lot.lotJobID,
							purchasedStatus: lot.purchasedStatus,
							soldDate: lot.soldDate,
							productType: lot.productType,
							noOfBedrooms: lot.noOfBedrooms,
							fullBathType: lot.fullBathType,
							halfBathType: lot.halfBathType,
							garageType: lot.garageType,
							size: lot.size,
							model: lot.model,
							elevation: lot.elevation,
							stories: lot.stories,
							street: lot.street,
							city: lot.city,
							province: lot.province,
							country: lot.country,
							postalCode: lot.postalCode,
							phase: lot.phase,
							hblot: lot.hblot,
						},
						user: {
							uniqueUserId: estateDetails.uniqueUserId,
							accountStatus: estateDetails.accountStatus,
							customerName,
							email,
						},
						legalContentFlags: {
							privacyPolicy: estateDetails.privacyPolicy,
							termsAndCondition: estateDetails.termsAndCondition,
							viewTutorial: estateDetails.viewTutorial,
						},
					}
					const showTutorial =
						estateDetails.viewTutorial.toLowerCase() !== 'yes' ? true : false

					if (lot) {
						const requestData = {
							uniqueUserId: estateDetails.uniqueUserId,
							email: encodedEmails[0],
							divisions: [
								{
									divisionMpcCd: division.divisionMpcCd,
									communities: [
										{
											communityMpcCd: community.communityMpcCd,
											marketingMpcCd: community.marketingMpcCd,
											lots: [
												{
													lotJobID: lot.lotJobID,
													purchasedStatus: lot.purchasedStatus,
												},
											],
										},
									],
								},
							],
						}

						dispatch({
							type: 'UPDATE_PRE_REQUEST_BODY',
							payload: requestData,
						})

						dispatch({
							type: 'UPDATE_SELECTED_LOT',
							payload: payload,
							uniqueUserId: estateDetails.uniqueUserId,
							showTutorial: showTutorial,
						})
						const { success, documents } =
							await documentService.getDocuments(requestData)

						dispatch({
							type: 'UPDATE_DOCUMENT_ARRAY',
							payload: success ? documents ?? [] : undefined,
						})
					} else {
						sessionStorage.clear()
						instance.logoutRedirect({
							postLogoutRedirectUri: '/error/notFound',
							idTokenHint: tempIdToken,
							onRedirectNavigate: () => {
								// Return false if you would like to stop navigation after local logout
								return true
							},
						})
					}
				}

				dispatch({ type: 'LOAD_COMPLETE' })
			} catch (err) {
				sessionStorage.clear()
				instance.logoutRedirect({
					postLogoutRedirectUri: '/error',
					idTokenHint: tempIdToken,
					onRedirectNavigate: () => {
						return true
					},
				})
			}
		}

		instance.addEventCallback(async (event) => {
			// set active account after redirect
			if (
				event.eventType === EventType.LOGIN_SUCCESS &&
				event.payload.account
			) {
				const account = event.payload.account
				instance.setActiveAccount(account)

				const idToken = event.payload.idToken
				tempIdToken = idToken
				sessionStorage.setItem('idToken', idToken)
				await userService
					.createToken({
						userName: window.btoa(account.username), // encode to base64
					})
					.then((response) => {
						sessionStorage.setItem('jwtToken', response.data.jwttoken)
						fetchData()
					})
					.catch(() => {
						sessionStorage.clear()
						instance.logoutRedirect({
							postLogoutRedirectUri: '/error',
							idTokenHint: idToken,
							onRedirectNavigate: () => {
								// Return false if you would like to stop navigation after local logout
								return true
							},
						})
					})
			} else if (
				event.eventType === EventType.LOGIN_FAILURE &&
				event.error.errorMessage.indexOf('Error: user_cancelled_authorize')
			) {
				await instance.loginRedirect({
					prompt: 'select_account',
				})
			}
		})

		// handle auth redired/do all initial setup for msal
		instance
			.handleRedirectPromise()
			// eslint-disable-next-line no-unused-vars
			.then(async (response) => {
				// Check if user signed in
				const account = instance.getActiveAccount()
				const jwtToken = sessionStorage.getItem('jwtToken')
				if (!account) {
					// redirect anonymous user to login page
					await instance.loginRedirect({
						prompt: 'select_account',
					})
				} else if (jwtToken) {
					fetchData()
				}
			})
			.catch((err) => {
				// TODO: Handle errors
				console.log('promise error', err)
			})
	}, [])

	useEffect(() => {
		showLoader()
	}, [])

	useEffect(() => {
		const scheduledMaintenanceAlertTitle = t(
			'service-message.scheduled-maintenance.alert-title'
		)
		const scheduledMaintenanceAlertMessage = t(
			'service-message.scheduled-maintenance.alert-message'
		)
		const scheduledMaintenanceStartDate = t(
			'service-message.scheduled-maintenance.start-date'
		)
		const scheduledMaintenanceEndDate = t(
			'service-message.scheduled-maintenance.end-date'
		)

		const isFlaged =
			sessionStorage.getItem('mhcpServiceMessageFlag') !== 'false'

		if (
			isFlaged &&
			scheduledMaintenanceStartDate &&
			scheduledMaintenanceEndDate &&
			Date.now() >= new Date(scheduledMaintenanceStartDate) &&
			Date.now() <= new Date(scheduledMaintenanceEndDate)
		) {
			showAlert(
				'info',
				scheduledMaintenanceAlertTitle,
				scheduledMaintenanceAlertMessage
			)
		}
	}, [])

	useEffect(() => {
		if (state.uniqueUserId && state.uniqueUserId !== '') {
			setTimeout(() => {
				hideLoader()
			}, 30000)
		}
	}, [state.uniqueUserId])

	useEffect(() => {
		if (state.loading || state.milestoneLoading) {
			// showLoader();
			// setTimeout(() => {
			//     hideLoader();
			// }, 30000);
		} else {
			hideLoader()
		}
	}, [state.loading, state.milestoneLoading])

	return (
		<>
			{state.userLoggedIn && (
				<LookupContext.Provider
					value={{
						...state,
						selectEstate,
						idToken,
					}}
				>
					<LookupDispatchContext.Provider value={dispatch}>
						{children}
					</LookupDispatchContext.Provider>
				</LookupContext.Provider>
			)}
		</>
	)
}

// Context hook
export const useLookupContext = () => {
	const context = useContext(LookupContext)
	return context
}
